import { formatCurrencyAmount } from '@/lib/formatMoney';

export interface ChurnRow {
  month: string;
  basis: 'exact' | 'approximated';
  groupsAtStart: number;
  groupsChurned: number;
  logoChurnRate: number | null;
  byCurrency: Record<string, { mrrAtStart: number; mrrChurned: number; revenueChurnRate: number | null }>;
}

// null is "nothing to divide by", not zero churn, so it never renders as 0.0%.
const asPercent = (rate: number | null) => (rate === null ? 'N/A' : `${(rate * 100).toFixed(1)}%`);

export function ChurnPanel({ row }: { row: ChurnRow | undefined }) {
  if (!row) {
    return <p className="py-8 text-center text-sm text-muted-foreground">Not enough history to measure churn yet.</p>;
  }

  const currencies = Object.entries(row.byCurrency);

  return (
    <div className="space-y-4" data-testid="churn-panel">
      <div>
        <p className="font-display text-3xl font-bold tracking-tight">{asPercent(row.logoChurnRate)}</p>
        <p className="text-sm text-muted-foreground">
          {row.groupsChurned} of {row.groupsAtStart} groups left in {row.month}
        </p>
      </div>

      <div className="space-y-1 text-sm">
        {currencies.map(([currency, figures]) => (
          <p key={currency}>
            {currency}: {asPercent(figures.revenueChurnRate)} revenue churn (
            {formatCurrencyAmount(figures.mrrChurned, currency)} of{' '}
            {formatCurrencyAmount(figures.mrrAtStart, currency)})
          </p>
        ))}
        {currencies.length === 0 && <p className="text-muted-foreground">No recurring revenue in this period.</p>}
      </div>

      {row.basis === 'approximated' && (
        <p className="text-xs text-muted-foreground">
          Reconstructed from subscription records. Figures for past months are approximate.
        </p>
      )}
    </div>
  );
}
